import { useQuery } from '@tanstack/react-query'
import { Printer } from 'lucide-react'
import { useState } from 'react'

import { downloadLoanTransactionReceipt, downloadSavingsTransactionReceipt } from '@/api/documents-api'
import { counterSavingsProductsQueryOptions } from '@/api/teller-api'
import type {
  CounterLoanAccount,
  CounterSavingsAccount,
  CounterSavingsPosting,
  CounterRepaymentPosted,
} from '@/api/teller-types'
import type { DualControlResult } from '@/api/types'
import { ViewDocumentButton } from '@/components/document-viewer'
import { MoneyDisplay } from '@/components/money-display'
import { VerificationGate } from '@/features/customers/verify-customer-dialog'
import { useHeldVerification } from '@/features/customers/verification-store'
import { MoneyInput } from '@/components/money-input'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { StatusBadge } from '@/components/status-badge'
import { formatBusinessDate } from '@/lib/datetime'
import type { Pesewas } from '@/lib/money'

import { LoanAccountPicker, SavingsAccountPicker } from './account-pickers'
import { savingsStatusNote } from './constants'
import { DualControlNotice, DualControlOutcome } from './dual-control'
import {
  useCounterDeposit,
  useCounterRepayment,
  useCounterSecurityDeposit,
  useCounterWithdrawal,
} from './mutations'
import { Field, Section } from './teller-panels'
import { TELLER_PERMISSIONS, useTellerPermissions } from './permissions'
import { TellerActionDialog } from './teller-action-dialog'
import type { PostingDay } from './use-posting-day'

/**
 * The four things a teller does with cash over a counter.
 *
 * They are four endpoints in three modules, and the differences between them are the whole
 * substance of this file:
 *
 *   - **Deposit** — `POST /savings/accounts/{id}/deposits`. Immediate. Refuses a security deposit
 *     account outright, before doing anything, with `savings.account_wrong_class`.
 *   - **Security deposit** — `POST /savings/accounts/{id}/security-deposit`. Immediate. The fifth
 *     counter cash-in operation, and the only rail that account class accepts. It raises the
 *     drawer's cash-in exactly as a deposit does.
 *   - **Withdrawal** — `POST /savings/accounts/{id}/withdrawals`. Countersigned, and checked
 *     against figures a teller cannot read.
 *   - **Repayment** — `POST /loan-cycles/{id}/repayments`. Immediate, and the split across penalty,
 *     fee, interest and principal is the server's, never this client's.
 *
 * Every one of them goes through the same cash drawer, which resolves the caller's own open session
 * and fails closed with `teller.no_open_till` when there is none. That is why the rails ask about a
 * session before they ask for an amount.
 */

/** A fresh idempotency key per attempt, so a double click cannot take the money twice. */
const newOperationId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : null

export type CounterRail = 'deposit' | 'security' | 'withdrawal' | 'repayment'

/**
 * The state every rail needs before it will let an amount be keyed: an open day and an open drawer.
 *
 * A session is not merely convenient. The drawer is resolved server-side from the caller's own open
 * session, so a teller without one is refused — and on the withdrawal rail that refusal arrives
 * *after* the countersignature, which is the worst possible time for it.
 */
function CounterReadiness({
  day,
  hasOpenSession,
  deferred,
}: {
  day: PostingDay
  hasOpenSession: boolean
  deferred?: boolean
}) {
  if (hasOpenSession) {
    return null
  }

  return (
    <Alert tone="warning">
      <div className="space-y-1">
        <AlertTitle>You have no open till</AlertTitle>
        <AlertDescription>
          Cash needs a drawer accountable for it, so this is refused with{' '}
          <span className="font-mono text-xs">teller.no_open_till</span>. Open a session on a counter
          till first
          {day.businessDate ? <> — the branch is working in {formatBusinessDate(day.businessDate)}</> : null}
          .
          {deferred ? (
            <>
              {' '}
              On this rail the check runs <em>after</em> the countersignature: the request would be
              accepted now, sit in the approvals queue, and fail when a supervisor releases it.
            </>
          ) : null}
        </AlertDescription>
      </div>
    </Alert>
  )
}

/** The account a teller has chosen, and everything about it that changes what happens next. */
function AccountCard({ account }: { account: CounterSavingsAccount }) {
  const statusNote = savingsStatusNote(account.status)

  return (
    <div className="space-y-3 rounded-card border border-border-subtle bg-surface-muted p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-heading">{account.customerName}</p>
          <p className="font-mono text-2xs text-muted">
            {account.accountNumber} · {account.customerNumber} · {account.productCode}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={account.productClass} />
          <StatusBadge status={account.status} />
        </div>
      </div>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
        <Field
          label="Ledger balance"
          value={
            <MoneyDisplay
              value={account.ledgerBalanceMinor}
              currency={account.currencyCode}
              className="text-left"
            />
          }
        />
        <Field
          label="On hold"
          value={
            <MoneyDisplay
              value={account.holdAmountMinor}
              currency={account.currencyCode}
              className="text-left"
            />
          }
          hint={account.holds.length > 0 ? `${String(account.holds.length)} hold(s) on this account.` : undefined}
        />
        <Field
          label="Available"
          value={
            <MoneyDisplay
              value={account.availableBalanceMinor}
              currency={account.currencyCode}
              className="text-left"
            />
          }
        />
        <Field
          label="Passbook"
          value={account.passbookNumber}
          mono
          hint={account.passbookNumber === null ? 'No passbook is recorded.' : undefined}
        />
      </dl>

      {statusNote ? (
        <Alert tone={account.status === 'Dormant' ? 'warning' : 'danger'}>
          <div className="space-y-1">
            <AlertTitle>{account.status}</AlertTitle>
            <AlertDescription>{statusNote}</AlertDescription>
          </div>
        </Alert>
      ) : null}
    </div>
  )
}

/**
 * A print-receipt button reused by both counter rails. Opens the receipt in the in-app viewer
 * rather than downloading it: the counter runs in kiosk mode, where a downloaded file is a file
 * nobody can open. The first request files the receipt; a later click is a reprint of that copy.
 */
function PrintReceiptButton({
  title,
  download,
}: {
  title: string
  download: () => Promise<{ blob: Blob; filename: string }>
}) {
  return (
    <ViewDocumentButton variant="secondary" size="sm" title={title} load={download}>
      <Printer />
      Print receipt
    </ViewDocumentButton>
  )
}

/** The receipt a teller reads back to the customer, off the response rather than off the form. */
function CashInReceipt({ posting, accountId }: { posting: CounterSavingsPosting; accountId: string }) {
  return (
    <div className="space-y-3">
      <Alert tone="success">
        <div className="space-y-1">
          <AlertTitle>
            {posting.wasReplay ? 'Already posted — the customer was charged once' : 'Posted'}
          </AlertTitle>
          <AlertDescription>
            <span className="font-mono text-xs">{posting.transaction.transactionNumber}</span> ·{' '}
            <MoneyDisplay
              value={posting.transaction.amountMinor}
              currency={posting.currencyCode}
              className="inline-block text-left"
            />{' '}
            taken on {formatBusinessDate(posting.transaction.businessDate)}. The balance is now{' '}
            <MoneyDisplay
              value={posting.balanceAfterMinor}
              currency={posting.currencyCode}
              className="inline-block text-left"
            />
            , of which{' '}
            <MoneyDisplay
              value={posting.availableBalanceMinor}
              currency={posting.currencyCode}
              className="inline-block text-left"
            />{' '}
            is available.
            {posting.wasReplay ? (
              <>
                {' '}
                This request repeated one the server had already seen, so it returned the original
                posting instead of taking the money a second time.
              </>
            ) : null}
            {posting.transaction.journalEntryNumber ? (
              <>
                {' '}
                Journal <span className="font-mono text-xs">{posting.transaction.journalEntryNumber}</span>.
              </>
            ) : null}
          </AlertDescription>
        </div>
      </Alert>
      <PrintReceiptButton
        title={`Receipt ${posting.transaction.transactionNumber}`}
        download={() => downloadSavingsTransactionReceipt(accountId, posting.transaction.id)}
      />
    </div>
  )
}

interface CashInRailProps {
  day: PostingDay
  branchId: string | null
  hasOpenSession: boolean
}

/**
 * Taking cash into an account — the deposit rail and the security deposit rail, which differ only
 * in the endpoint they post to and the account class they accept.
 */
function CashInRail({
  day,
  branchId,
  hasOpenSession,
  variant,
}: CashInRailProps & { variant: 'deposit' | 'security' }) {
  const permissions = useTellerPermissions()
  const [account, setAccount] = useState<CounterSavingsAccount | null>(null)
  const [amount, setAmount] = useState<Pesewas | null>(null)
  const [narration, setNarration] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [operationId, setOperationId] = useState<string | null>(null)

  const isSecurity = variant === 'security'
  const deposit = useCounterDeposit(account?.id ?? '')
  // Offered, never demanded, on cash coming in — but when the teller has verified, the posting
  // carries the proof rather than discarding it.
  const heldVerification = useHeldVerification(account?.customerId ?? '')
  const security = useCounterSecurityDeposit(account?.id ?? '')
  const mutation = isSecurity ? security : deposit

  const permitted = isSecurity ? permissions.canTopUpSecurityDeposit : permissions.canPostDeposit
  const expectedClass = isSecurity ? 'SecurityDeposit' : 'RegularSavings'
  const wrongClass = account !== null && account.productClass !== expectedClass

  const blocked =
    !permitted || !day.acceptsPostings || account === null || wrongClass || amount === null || amount <= 0

  return (
    <div className="space-y-4">
      {!permitted ? (
        <Alert tone="warning">
          <div className="space-y-1">
            <AlertTitle>This rail is not open to your role</AlertTitle>
            <AlertDescription>
              Taking cash here needs{' '}
              <span className="font-mono text-xs">
                {isSecurity ? TELLER_PERMISSIONS.securityDepositTopUp : TELLER_PERMISSIONS.savingsDepositPost}
              </span>{' '}
              and <span className="font-mono text-xs">{TELLER_PERMISSIONS.depositPost}</span>, the
              second of which is checked by the cash drawer once it knows the drawer is a counter.
            </AlertDescription>
          </div>
        </Alert>
      ) : null}

      <CounterReadiness day={day} hasOpenSession={hasOpenSession} />

      <SavingsAccountPicker
        branchId={branchId}
        productClass={expectedClass}
        selectedId={account?.id ?? null}
        canView={permissions.canViewSavings}
        onSelect={(chosen) => {
          setAccount(chosen)
          mutation.reset()
        }}
      />

      {account ? <AccountCard account={account} /> : null}

      {/* Optional on money coming IN — offered, never blocking, same as the savings cash screen. */}
      {account && !wrongClass ? (
        <VerificationGate
          customerId={account.customerId}
          customerName={account.customerName}
          required={false}
        />
      ) : null}

      {wrongClass && account ? (
        <Alert tone="danger">
          <div className="space-y-1">
            <AlertTitle>Wrong rail for this account</AlertTitle>
            <AlertDescription>
              {account.accountNumber} is a{' '}
              {account.productClass === 'SecurityDeposit' ? 'security deposit' : 'regular savings'}{' '}
              account.{' '}
              {account.productClass === 'SecurityDeposit'
                ? 'An ordinary deposit against it is refused with savings.account_wrong_class before anything happens — take it on the security deposit rail instead.'
                : 'The security deposit rail only accepts collateral accounts — take this on the deposit rail instead.'}
            </AlertDescription>
          </div>
        </Alert>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`cash-in-amount-${variant}`}>Amount taken</Label>
          <MoneyInput
            id={`cash-in-amount-${variant}`}
            value={amount}
            onChange={setAmount}
            currency={account?.currencyCode}
            disabled={!permitted || account === null}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`cash-in-narration-${variant}`}>Narration</Label>
          <Input
            id={`cash-in-narration-${variant}`}
            value={narration}
            autoComplete="off"
            onChange={(event) => setNarration(event.target.value)}
            disabled={!permitted || account === null}
          />
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          variant="primary"
          disabled={blocked}
          onClick={() => {
            setOperationId(newOperationId())
            setConfirming(true)
          }}
        >
          {isSecurity ? 'Take the collateral' : 'Take the deposit'}
        </Button>
      </div>

      <TellerActionDialog
        open={confirming}
        onOpenChange={(next) => {
          setConfirming(next)
          if (!next && mutation.isSuccess) {
            // A completed posting clears the counter for the next customer; the receipt has been
            // read back by now, and leaving the amount on screen invites it being posted again.
            setAmount(null)
            setNarration('')
            mutation.reset()
          }
        }}
        title={isSecurity ? 'Take cash collateral' : 'Take a deposit'}
        description={
          account
            ? `${account.customerName} · ${account.accountNumber}`
            : 'No account is selected.'
        }
        confirmLabel="Post it"
        closeLabel="Next customer"
        isPending={mutation.isPending}
        error={mutation.isError ? mutation.error : null}
        outcome={
          mutation.data ? (
            <CashInReceipt posting={mutation.data} accountId={account?.id ?? ''} />
          ) : undefined
        }
        onConfirm={() => {
          if (!account || amount === null) {
            return
          }

          mutation.mutate({
            amountMinor: amount,
            currencyCode: account.currencyCode,
            // The ledger's date, taken from the business day and never from this machine.
            valueDate: day.businessDate,
            narration: narration.trim() === '' ? null : narration.trim(),
            clientOperationId: operationId,
            verificationId: heldVerification?.id ?? null,
          })
        }}
      >
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
          <Field
            label="Amount"
            value={
              <MoneyDisplay
                value={amount}
                currency={account?.currencyCode}
                emphasis="strong"
                className="text-left"
              />
            }
          />
          <Field
            label="Business date"
            value={day.businessDate ? formatBusinessDate(day.businessDate) : null}
            hint={day.businessDate ? undefined : 'No day is open, so nothing will post.'}
          />
          <Field label="Account" value={account?.accountNumber} mono />
          <Field label="Product" value={account?.productCode} mono />
        </dl>
        <p className="text-xs text-muted">
          Count the cash before confirming. This takes effect at once — it is not countersigned — and
          a reversal is a manager&apos;s action, not yours.
        </p>
      </TellerActionDialog>
    </div>
  )
}

export function DepositRail(props: CashInRailProps) {
  return <CashInRail {...props} variant="deposit" />
}

export function SecurityDepositRail(props: CashInRailProps) {
  return <CashInRail {...props} variant="security" />
}

/**
 * Paying savings out.
 *
 * Two things about this rail are wrong on every screen that treats the available balance as the
 * ceiling, and both are stated here instead.
 *
 * The server checks `amount + product.withdrawalFee` against the available balance and then applies
 * the product's minimum balance on top. Both figures live on the savings product, which is read
 * under `savings.product.view` — a permission the shipped Teller role does not hold. So a teller
 * keying the full available balance is refused, and if a fee applies the customer is debited more
 * than the amount they were told. Where the permission *is* held, the two figures are fetched and
 * shown; where it is not, the rail says the ceiling is not knowable here rather than implying one.
 *
 * And the whole thing is countersigned, with the custody checks running after the gate: a teller
 * with no open session can be told "sent for countersignature" and have it fail at approval time.
 */
export function WithdrawalRail({ day, branchId, hasOpenSession }: CashInRailProps) {
  const permissions = useTellerPermissions()
  const [account, setAccount] = useState<CounterSavingsAccount | null>(null)
  const [amount, setAmount] = useState<Pesewas | null>(null)
  const [narration, setNarration] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [operationId, setOperationId] = useState<string | null>(null)

  const withdraw = useCounterWithdrawal(account?.id ?? '')
  // The verification the gate below records is held per customer; the posting must carry its id —
  // the server correlates by identifier, never by "a verification probably happened".
  const heldVerification = useHeldVerification(account?.customerId ?? '')

  const products = useQuery(counterSavingsProductsQueryOptions(permissions.canViewSavingsProducts))
  const product = account ? (products.data ?? []).find((candidate) => candidate.id === account.productId) : undefined

  const fee = product?.withdrawalFeeMinor ?? null
  const minBalance = product?.minBalanceMinor ?? null

  /**
   * The most this account can pay out, where the product is readable.
   *
   * Two separate refusals, not one, and they are measured against different balances — which is why
   * this is a `min` rather than a subtraction of both terms from the same figure.
   * `SavingsAccount.Withdraw` checks `amount + fee` against the **available** balance
   * (`savings.insufficient_available_balance`) and then checks the **ledger** balance after the
   * debit against the product's minimum (`savings.below_minimum_balance`). Holds depress the first
   * and not the second, so subtracting the minimum from the available balance would understate the
   * ceiling on any account carrying a hold and send a customer away with less than they can have.
   *
   * Null where any part of it is unknown — and a null here is rendered as "not knowable", never as
   * the available balance.
   */
  const payableCeiling =
    account && product
      ? Math.min(
          account.availableBalanceMinor - (fee ?? 0),
          account.ledgerBalanceMinor - (fee ?? 0) - (minBalance ?? 0),
        )
      : null

  const dormant = account?.status === 'Dormant'
  const notActive = account !== null && account.status !== 'Active'
  const notWithdrawable = account !== null && !account.isWithdrawable

  const blocked =
    !permissions.canPostWithdrawal ||
    !day.acceptsPostings ||
    account === null ||
    notActive ||
    notWithdrawable ||
    amount === null ||
    amount <= 0

  return (
    <div className="space-y-4">
      {!permissions.canPostWithdrawal ? (
        <Alert tone="warning">
          <div className="space-y-1">
            <AlertTitle>This rail is not open to your role</AlertTitle>
            <AlertDescription>
              Paying savings out needs{' '}
              <span className="font-mono text-xs">{TELLER_PERMISSIONS.savingsWithdrawalPost}</span> and{' '}
              <span className="font-mono text-xs">{TELLER_PERMISSIONS.withdrawalPost}</span>. Note
              that the drawer&apos;s own refusal is{' '}
              <span className="font-mono text-xs">auth.permission_denied</span>, not a teller code.
            </AlertDescription>
          </div>
        </Alert>
      ) : null}

      <CounterReadiness day={day} hasOpenSession={hasOpenSession} deferred />

      <SavingsAccountPicker
        branchId={branchId}
        productClass="RegularSavings"
        selectedId={account?.id ?? null}
        canView={permissions.canViewSavings}
        onSelect={(chosen) => {
          setAccount(chosen)
          withdraw.reset()
        }}
      />

      {account ? <AccountCard account={account} /> : null}

      {/*
        The server refuses a withdrawal without a fresh verification
        (customers.verification_required). The counter is where the member is standing, so the
        counter is where the verifying happens — this gate was on the savings cash screen and
        absent here, which left a teller blocked with no door. Found by UAT, not by tests.
      */}
      {account && !notWithdrawable ? (
        <VerificationGate
          customerId={account.customerId}
          customerName={account.customerName}
          required
        />
      ) : null}

      {notWithdrawable && account ? (
        <Alert tone="danger">
          <div className="space-y-1">
            <AlertTitle>This account cannot be withdrawn from</AlertTitle>
            <AlertDescription>
              A security deposit is released by an offset or at closure, never over a counter —{' '}
              <span className="font-mono text-xs">savings.account_not_withdrawable</span>.
            </AlertDescription>
          </div>
        </Alert>
      ) : dormant ? (
        <Alert tone="warning">
          <div className="space-y-1">
            <AlertTitle>A dormant account cannot pay out</AlertTitle>
            <AlertDescription>
              The refusal is <span className="font-mono text-xs">savings.account_dormant</span>, and
              reactivating needs <span className="font-mono text-xs">savings.dormancy.manage</span> —
              a supervisor, not a teller. Send the customer to one before keying an amount.
            </AlertDescription>
          </div>
        </Alert>
      ) : null}

      <Alert tone={product ? 'info' : 'warning'}>
        <div className="space-y-1">
          <AlertTitle>
            {product ? 'What this withdrawal is checked against' : 'The ceiling is not visible to you'}
          </AlertTitle>
          <AlertDescription>
            {product ? (
              <>
                The server checks the amount plus the withdrawal fee (
                <MoneyDisplay
                  value={fee}
                  currency={product.currencyCode}
                  className="inline-block text-left"
                />
                ) against the available balance, and then applies the product&apos;s minimum balance (
                <MoneyDisplay
                  value={minBalance}
                  currency={product.currencyCode}
                  className="inline-block text-left"
                />
                ). The most this account can pay out is therefore{' '}
                <MoneyDisplay
                  value={payableCeiling}
                  currency={product.currencyCode}
                  className="inline-block text-left"
                />
                , not the available balance. The fee is debited on top of the amount.
              </>
            ) : (
              <>
                A withdrawal is checked against the amount <em>plus</em> the product&apos;s withdrawal
                fee, and then against the product&apos;s minimum balance. Both live on the savings
                product, read under{' '}
                <span className="font-mono text-xs">{TELLER_PERMISSIONS.savingsProductView}</span>,
                which your role does not hold — so the available balance above is not the amount that
                can be paid out, and a customer may be debited more than the figure keyed here. If a
                withdrawal is refused with{' '}
                <span className="font-mono text-xs">savings.insufficient_available_balance</span> or{' '}
                <span className="font-mono text-xs">savings.below_minimum_balance</span>, ask a
                supervisor for the shortfall rather than trying a smaller figure blind.
              </>
            )}
          </AlertDescription>
        </div>
      </Alert>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="withdrawal-amount">Amount to pay out</Label>
          <MoneyInput
            id="withdrawal-amount"
            value={amount}
            onChange={setAmount}
            currency={account?.currencyCode}
            disabled={!permissions.canPostWithdrawal || account === null}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="withdrawal-narration">Narration</Label>
          <Input
            id="withdrawal-narration"
            value={narration}
            autoComplete="off"
            onChange={(event) => setNarration(event.target.value)}
            disabled={!permissions.canPostWithdrawal || account === null}
          />
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          variant="primary"
          disabled={blocked}
          onClick={() => {
            setOperationId(newOperationId())
            setConfirming(true)
          }}
        >
          Submit the withdrawal
        </Button>
      </div>

      <TellerActionDialog
        open={confirming}
        onOpenChange={(next) => {
          setConfirming(next)
          if (!next && withdraw.isSuccess) {
            setAmount(null)
            setNarration('')
            withdraw.reset()
          }
        }}
        title="Pay savings out"
        description={account ? `${account.customerName} · ${account.accountNumber}` : 'No account is selected.'}
        confirmLabel="Submit it"
        closeLabel="Next customer"
        isPending={withdraw.isPending}
        error={withdraw.isError ? withdraw.error : null}
        notice={
          <DualControlNotice
            action="Paying savings out"
            approvePermission={TELLER_PERMISSIONS.savingsWithdrawalPostApprove}
            // Read, not assumed. This is a claim about the operator's own role, and the pair is not
            // barred in the segregation matrix — a role can hold both halves, and telling somebody
            // who does that they do not is a lie the screen has no need to tell.
            canCountersign={permissions.canApproveWithdrawal}
          >
            {' '}
            Do not hand over cash until it has been countersigned.
          </DualControlNotice>
        }
        outcome={
          withdraw.data ? (
            <WithdrawalOutcome result={withdraw.data} hasOpenSession={hasOpenSession} />
          ) : undefined
        }
        onConfirm={() => {
          if (!account || amount === null) {
            return
          }

          withdraw.mutate({
            amountMinor: amount,
            currencyCode: account.currencyCode,
            valueDate: day.businessDate,
            narration: narration.trim() === '' ? null : narration.trim(),
            clientOperationId: operationId,
            verificationId: heldVerification?.id ?? null,
          })
        }}
      >
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
          <Field
            label="Amount keyed"
            value={
              <MoneyDisplay
                value={amount}
                currency={account?.currencyCode}
                emphasis="strong"
                className="text-left"
              />
            }
          />
          <Field
            label="Fee on top"
            value={
              product ? (
                <MoneyDisplay
                  value={fee}
                  currency={account?.currencyCode}
                  className="text-left"
                />
              ) : (
                'Not visible to your role'
              )
            }
            hint={
              product
                ? 'Debited in addition to the amount above.'
                : 'The customer may be debited more than the amount keyed.'
            }
          />
          <Field label="Account" value={account?.accountNumber} mono />
          <Field
            label="Business date"
            value={day.businessDate ? formatBusinessDate(day.businessDate) : null}
          />
        </dl>
      </TellerActionDialog>
    </div>
  )
}

function WithdrawalOutcome({
  result,
  hasOpenSession,
}: {
  result: DualControlResult
  hasOpenSession: boolean
}) {
  return (
    <DualControlOutcome
      result={result}
      awaitingDescription="Nothing has left the drawer and the customer's balance is unchanged."
      appliedDescription="The cash has been paid out and the account is debited."
      deferredWarning={
        <span className="text-xs">
          The custody checks run when it is released, not now — the available balance, the fee, the
          minimum balance, and whether a drawer is open at all.
          {hasOpenSession
            ? ''
            : ' You have no open session, so this will fail with teller.no_open_till at approval time unless one is open by then.'}
        </span>
      }
    />
  )
}

/**
 * Taking a collection against a loan round.
 *
 * The amount may not exceed what the round owes: the server refuses with
 * `loans.repayment_exceeds_outstanding` and names the exact figure that clears it. Money that
 * belongs in a member's savings is deposited by the savings rail, not left as an overpayment here.
 */
export function RepaymentRail({ day, branchId, hasOpenSession }: CashInRailProps) {
  const permissions = useTellerPermissions()
  const [account, setAccount] = useState<CounterLoanAccount | null>(null)
  const [amount, setAmount] = useState<Pesewas | null>(null)
  const [narration, setNarration] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [operationId, setOperationId] = useState<string | null>(null)

  const cycle = account?.currentCycle ?? null
  const repay = useCounterRepayment(cycle?.id ?? '')

  const exceedsOutstanding = cycle !== null && amount !== null && amount > cycle.totalOutstandingMinor

  const blocked =
    !permissions.canPostRepayment ||
    !day.acceptsPostings ||
    cycle === null ||
    amount === null ||
    amount <= 0 ||
    exceedsOutstanding ||
    day.businessDate === null

  return (
    <div className="space-y-4">
      {!permissions.canPostRepayment ? (
        <Alert tone="warning">
          <div className="space-y-1">
            <AlertTitle>This rail is not open to your role</AlertTitle>
            <AlertDescription>
              Taking a collection needs{' '}
              <span className="font-mono text-xs">{TELLER_PERMISSIONS.loansRepaymentPost}</span> and{' '}
              <span className="font-mono text-xs">{TELLER_PERMISSIONS.depositPost}</span> — the
              second because a collection reaches the drawer as an ordinary cash-in, whatever caused
              it. <span className="font-mono text-xs">{TELLER_PERMISSIONS.repaymentPost}</span> is in
              the catalogue but no endpoint checks it.
            </AlertDescription>
          </div>
        </Alert>
      ) : null}

      <CounterReadiness day={day} hasOpenSession={hasOpenSession} />

      <LoanAccountPicker
        branchId={branchId}
        selectedId={account?.id ?? null}
        canView={permissions.canViewLoans}
        onSelect={(chosen) => {
          setAccount(chosen)
          repay.reset()
        }}
      />

      {cycle ? (
        <div className="space-y-3 rounded-card border border-border-subtle bg-surface-muted p-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-heading">{account?.customerName}</p>
              <p className="font-mono text-2xs text-muted">
                {account?.accountNumber} · {cycle.displayReference}
              </p>
            </div>
            <StatusBadge status={cycle.status} />
          </div>

          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
            <Field
              label="Total outstanding"
              value={
                <MoneyDisplay
                  value={cycle.totalOutstandingMinor}
                  currency={cycle.currencyCode}
                  className="text-left"
                />
              }
            />
            <Field
              label="In arrears"
              value={
                <MoneyDisplay
                  value={cycle.amountInArrearsMinor}
                  currency={cycle.currencyCode}
                  className="text-left"
                />
              }
              hint={cycle.daysInArrears > 0 ? `${String(cycle.daysInArrears)} days late.` : 'Not in arrears.'}
            />
            <Field
              label="Penalty owed"
              value={
                <MoneyDisplay
                  value={cycle.penaltyOutstandingMinor}
                  currency={cycle.currencyCode}
                  className="text-left"
                />
              }
              hint="Cleared first — the split is the server's."
            />
            <Field
              label="Classification"
              value={cycle.classificationCode}
              hint={cycle.classificationCode === null ? 'This round has not been classified.' : undefined}
            />
          </dl>
        </div>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="repayment-amount">Amount collected</Label>
          <MoneyInput
            id="repayment-amount"
            value={amount}
            onChange={setAmount}
            currency={cycle?.currencyCode}
            disabled={!permissions.canPostRepayment || cycle === null}
          />
          {exceedsOutstanding && cycle ? (
            <p className="text-xs text-danger-text">
              More than the round owes. The most that can be taken is{' '}
              <MoneyDisplay
                value={cycle.totalOutstandingMinor}
                currency={cycle.currencyCode}
                className="inline-block text-left"
              />
              ; anything beyond it belongs in the member&apos;s savings.
            </p>
          ) : null}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="repayment-narration">Narration</Label>
          <Input
            id="repayment-narration"
            value={narration}
            autoComplete="off"
            onChange={(event) => setNarration(event.target.value)}
            disabled={!permissions.canPostRepayment || cycle === null}
          />
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          variant="primary"
          disabled={blocked}
          onClick={() => {
            setOperationId(newOperationId())
            setConfirming(true)
          }}
        >
          Take the collection
        </Button>
      </div>

      <TellerActionDialog
        open={confirming}
        onOpenChange={(next) => {
          setConfirming(next)
          if (!next && repay.isSuccess) {
            setAmount(null)
            setNarration('')
            repay.reset()
          }
        }}
        title="Take a collection"
        description={
          cycle ? `${account?.customerName ?? ''} · ${cycle.displayReference}` : 'No round is selected.'
        }
        confirmLabel="Post it"
        closeLabel="Next customer"
        isPending={repay.isPending}
        error={repay.isError ? repay.error : null}
        outcome={repay.data ? <RepaymentReceipt posted={repay.data} /> : undefined}
        onConfirm={() => {
          if (!cycle || amount === null || day.businessDate === null) {
            return
          }

          repay.mutate({
            amountMinor: amount,
            valueDate: day.businessDate,
            // Always the counter drawer here. A field till is never given a session, so a shift
            // open on this screen is a counter shift by construction; field collections are posted
            // from the mobile application, where the officer and the drawer are the same fact.
            channel: 'TellerTill',
            // Always null — see the note on CounterRepaymentRequest. The drawer is resolved from
            // the caller's own open session.
            tillId: null,
            clientOperationId: operationId,
            groupMeetingId: null,
            narration: narration.trim() === '' ? null : narration.trim(),
          })
        }}
      >
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
          <Field
            label="Amount"
            value={
              <MoneyDisplay
                value={amount}
                currency={cycle?.currencyCode}
                emphasis="strong"
                className="text-left"
              />
            }
          />
          <Field
            label="Value date"
            value={day.businessDate ? formatBusinessDate(day.businessDate) : null}
            hint={day.businessDate ? undefined : 'No day is open, so nothing will post.'}
          />
          <Field label="Round" value={cycle?.displayReference} mono />
          <Field
            label="Outstanding before"
            value={
              <MoneyDisplay
                value={cycle?.totalOutstandingMinor ?? null}
                currency={cycle?.currencyCode}
                className="text-left"
              />
            }
          />
        </dl>
        <p className="text-xs text-muted">
          The split across penalty, fee, interest and principal is computed by the server when this
          posts. It is shown on the receipt, not predicted here.
        </p>
      </TellerActionDialog>
    </div>
  )
}

function RepaymentReceipt({ posted }: { posted: CounterRepaymentPosted }) {
  return (
    <div className="space-y-3">
      <Alert tone="success">
        <div className="space-y-1">
          <AlertTitle>{posted.cycleClosed ? 'Posted — the round is now closed' : 'Posted'}</AlertTitle>
          <AlertDescription>
            <MoneyDisplay
              value={posted.amountMinor}
              currency={posted.currencyCode}
              className="inline-block text-left"
            />{' '}
            collected on {formatBusinessDate(posted.valueDate)}.
            {posted.journalEntry ? (
              <>
                {' '}
                Journal <span className="font-mono text-xs">{posted.journalEntry}</span>.
              </>
            ) : null}
          </AlertDescription>
        </div>
      </Alert>

      <PrintReceiptButton
        title={`Receipt ${posted.journalEntry ?? posted.transactionId}`}
        download={() => downloadLoanTransactionReceipt(posted.cycleId, posted.transactionId)}
      />

      <Section title="How it was applied" description="The server's allocation, in its own order.">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
          <Field
            label="Penalty"
            value={
              <MoneyDisplay
                value={posted.allocation.penaltyMinor}
                currency={posted.currencyCode}
                className="text-left"
              />
            }
          />
          <Field
            label="Fees"
            value={
              <MoneyDisplay
                value={posted.allocation.feeMinor}
                currency={posted.currencyCode}
                className="text-left"
              />
            }
          />
          <Field
            label="Interest"
            value={
              <MoneyDisplay
                value={posted.allocation.interestMinor}
                currency={posted.currencyCode}
                className="text-left"
              />
            }
          />
          <Field
            label="Principal"
            value={
              <MoneyDisplay
                value={posted.allocation.principalMinor}
                currency={posted.currencyCode}
                className="text-left"
              />
            }
          />
        </dl>

        <p className="mt-3 text-xs text-muted">
          Outstanding is now{' '}
          <MoneyDisplay
            value={posted.cycle.totalOutstandingMinor}
            currency={posted.cycle.currencyCode}
            className="inline-block text-left"
          />
          {posted.cycle.daysInArrears > 0
            ? `, and the round is ${String(posted.cycle.daysInArrears)} days in arrears after the payment.`
            : ', and the round is not in arrears.'}
        </p>
      </Section>
    </div>
  )
}
